/**
 * Today's business date (SRV-L-02). New documents default to the date of the branch's time zone (else the tenant's),
 * not the UTC date of `toISOString()`: between 00:00 and 03:00 in Amman the UTC date is still yesterday, and on the first
 * of a month that puts the document in the previous, possibly closed, period. The session sets the zone once it is
 * loaded; before that (and when the zone is unknown to the browser) the browser's local date is used. The server
 * validates the date in any case.
 */
let businessTimeZone: string | undefined;

/** Sets the time zone of the business date (the session's branch or tenant zone); `undefined` = the browser's zone. */
export function setBusinessTimeZone(timeZone: string | undefined): void {
  businessTimeZone = timeZone;
}

/** The ISO date (yyyy-MM-dd) of `now` in the business time zone. */
export function businessToday(now: Date = new Date(), timeZone: string | undefined = businessTimeZone): string {
  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(now);
      const part = (type: string) => parts.find((item) => item.type === type)?.value ?? "";
      return `${part("year")}-${part("month")}-${part("day")}`;
    } catch {
      // An unknown zone falls back to the browser's local date.
    }
  }
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${String(now.getFullYear())}-${month}-${day}`;
}

/** Year and month (1-12) of today's business date: the default period of month and year pickers. */
export function businessMonth(now: Date = new Date(), timeZone: string | undefined = businessTimeZone) {
  const [year = "1970", month = "1"] = businessToday(now, timeZone).split("-");
  return { year: Number(year), month: Number(month) };
}
